import React, { useEffect, useRef } from 'react';
import { prop } from 'ramda';
import { Icon } from 'js/components/common/Icon';
import { cls } from 'js/utils/classNameBuilder.util';
import { useSmartTranslations } from 'js/hooks/i18n';
import { getCleanUrl } from 'js/utils/url.util';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';
import { useCombinedRefs } from '@theme/js/hooks/swiss/useCombinedRefs';

import { ActionButtons } from 'js/components/common/Popup/ActionButtons';
import style from './Popup.scss';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@core/js/components/common/Popup/ShallowPopup';

const Controls = ({ onPrev, onNext, label }) => {
    const rightIcon = onNext ? 'circleArrowRight' : 'circleArrowRightDisabled';
    const leftIcon = onPrev ? 'circleArrowLeft' : 'circleArrowLeftDisabled';

    return (
        <div className={style.controls}>
            <Icon type={leftIcon} onClick={onPrev} />
            <div>
                {label}
            </div>
            <Icon type={rightIcon} onClick={onNext} />
        </div>
    );
};

export const Buttons = ({ buttons, className = '' }) => {
    if (React.isValidElement(buttons)) {
        return buttons;
    }

    if (buttons) {
        return <ActionButtons src={buttons} className={className} />;
    }

    return null;
};

/**
 * ShallowPopup
 * @description General popup layout block. While being popup it does not center itself or have any overlays. Acts as
 * base component for conventional popups, providing some common layout, buttons and icons handling.
 */
export const ShallowPopup = ({
    id,
    onClose,
    buttons,
    children,
    controls,
    additionalContent,
    title = '',
    onTouchStart,
    className = '',
    classNames = {},
    isModal = false,
    style: popupStyle,
    iconType = 'closePopup',
    refs: { popupRef, innerPopupRef } = {},
    isBonusWithGamesPopup
}) => {
    const t = useSmartTranslations();
    const buildCls = cls([style, classNames]);
    const titleRegister = getCleanUrl();
    const isMobile = useDeviceSelector(prop('isMobile'));
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const isRegisterPage = titleRegister === '/register';
    const isRegisterMobile = isMobile && isRegisterPage;
    const isRegisterDesktop = !isMobile && isRegisterPage;
    const isPageInPopup = children?.props?.page;
    const isPagePopupDesktop = isPageInPopup && isRegisterDesktop;
    const isPagePopupMobile = isPageInPopup && isRegisterMobile;
    const isPokerPage = children?.props?.page?.title;
    const pokerPopups = dynamicTags && dynamicTags.DTAG_POKERPOPUPS;
    const isPokerPopup = pokerPopups && isPokerPage && pokerPopups.includes(isPokerPage);
    const popupClassName = 'confirmation_close_reg_popup';
    const reconnectPopupClassName = 'reconnect-popup';

    const focusRef = useRef();
    const combinedRef = useCombinedRefs(focusRef, popupRef);

    useEffect(() => {
        focusRef?.current.focus();
    }, [focusRef]);

    return (
        <div className={`${isRegisterMobile
            ? `${style.registrationPopup}`
            : isRegisterDesktop && `${style.registrationDesktopPopup}`} ${!isPageInPopup && isRegisterMobile
            ? `${style.registerMobileTermsPopup}`
            : !isPageInPopup && isRegisterDesktop
                ? `${style.registerDesktopTermsPopup}`
                : isPokerPopup
                    ? `${style.pokerPopup}`
                    : ''} ${style.popup} ${className}`}
            ref={combinedRef}
            onTouchStart={onTouchStart}
            {...{ id, style: popupStyle }}
            tabIndex={0}
        >
            <div className={buildCls(['header'])}>
                {title && !isRegisterPage && <div className={`popup-modal__title ${style.title}`}>
                    {t(title)}
                </div>}
                {/* because of useNavigateToPopupBackgroundPage.js */}
                {title && className === popupClassName && <div className={`popup-modal__title ${style.title}`}>
                    {t(title)}
                </div>}
                {title && className === reconnectPopupClassName && isRegisterPage && <div className={`popup-modal__title ${style.title}`}>
                    {t(title)}
                </div>}
            </div>
            <div className={`popup-modal__inner-content ${buildCls(['inner'])}`} ref={innerPopupRef}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    {children}
                </ErrorBoundary>
            </div>
            {isBonusWithGamesPopup && isMobile && (
                <Buttons buttons={buttons} className={style.withGamesBonusBtn} />
            )}
            {isBonusWithGamesPopup && !isMobile && (
                <Buttons buttons={buttons} className={classNames.actionButtons} />
            )}
            {!isBonusWithGamesPopup && (
                <Buttons buttons={buttons} className={classNames.actionButtons} />
            )}
            {!isModal && (
                <Icon
                    type={iconType}
                    className={`${isPagePopupDesktop
                        ? style.registerClose
                        : isPageInPopup && isRegisterDesktop
                            ? style.registerTermsClose
                            : isPokerPopup
                                ? `${style.pokerClose}`
                                : buildCls(['close'])}${isPagePopupMobile ? ` ${style.mobileRegisterClose}` : ''}`}
                    onClick={onClose}
                    aria-label={t('playtech.mobile.system.popup.aria-label.close')}
                />
            )}
            {Boolean(controls) && <Controls {...controls} />}
            {additionalContent && <div dangerouslySetInnerHTML={{ __html: additionalContent }}></div>}
        </div>
    );
};
